import { useId } from 'react';
import { Button, Card, CardContent, CardTitle } from '@/shared/components';
import type { TemplateVersion, TemplateVersionSummary } from '../../types/documentTemplates.types';
import { actorLabel, formatDay } from '../../utils/versionLabels';

interface Props {
  published: TemplateVersionSummary | null;
  draft: TemplateVersion | null;
  hasDraft: boolean;
  onOpenDraft?: () => void;
}

function StatusDot({ tone }: { tone: 'success' | 'warning' | 'muted' }) {
  const colour = { success: 'bg-mp-success', warning: 'bg-mp-warning', muted: 'bg-mp-input' }[tone];
  return <span aria-hidden="true" className={`mt-1.5 size-2.5 shrink-0 rounded-full ${colour}`} />;
}

function PublishedRow({ published }: { published: TemplateVersionSummary | null }) {
  if (!published) {
    return (
      <div className="flex gap-3">
        <StatusDot tone="muted" />
        <p className="text-sm text-mp-muted">Not published yet</p>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <StatusDot tone="success" />
      <div className="min-w-0 text-sm">
        <p className="font-medium">Published v{published.versionNumber}</p>
        <p className="text-mp-muted">
          {formatDay(published.publishedAt)} by {actorLabel(published.publishedByName)}
        </p>
        {published.changeNote && <p className="mt-1.5 break-words">&ldquo;{published.changeNote}&rdquo;</p>}
      </div>
    </div>
  );
}

function DraftRow({ draft, hasDraft, onOpenDraft }: Omit<Props, 'published'>) {
  if (!hasDraft) {
    return (
      <div className="flex gap-3">
        <StatusDot tone="muted" />
        <p className="text-sm text-mp-muted">No draft in progress</p>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <StatusDot tone="warning" />
      <div className="min-w-0 flex-1 text-sm">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-medium">{draft ? `Draft, revision ${draft.revision}` : 'Draft in progress'}</p>
          {onOpenDraft && (
            <Button type="button" variant="clear" size="none" className="text-mp-link hover:underline" onClick={onOpenDraft}>
              Open draft
            </Button>
          )}
        </div>
        {draft && (
          <p className="text-mp-muted">
            Last edited {formatDay(draft.updatedAt ?? draft.createdAt)} by{' '}
            {actorLabel(draft.updatedByName ?? draft.createdByName)}
          </p>
        )}
      </div>
    </div>
  );
}

export function VersionStatusCard({ published, draft, hasDraft, onOpenDraft }: Props) {
  const headingId = useId();
  return (
    <Card ariaLabelledBy={headingId}>
      <CardContent className="space-y-3 py-5">
        <CardTitle size="sm" id={headingId}>
          Versions
        </CardTitle>
        <PublishedRow published={published} />
        <hr className="border-mp-border" />
        <DraftRow draft={draft} hasDraft={hasDraft} onOpenDraft={onOpenDraft} />
      </CardContent>
    </Card>
  );
}
